/**
 * Where a finished mission is recorded. The decision (learn-r2, answer 3): per person on cpg.computer's API, so a
 * tick follows the founder across the preview, the live app and their phone. Only the mission id, the time and how
 * it ticked (`auto` or `self`) are sent; never what was changed.
 *
 * How a tick travels: this browser → the app's own server (`GET`/`POST /api/learn/ticks`, behind the access guard)
 * → cpg.computer's API, with the app's credential (CPG_APP_TOKEN) and the person's sign-in token to say who it is.
 * The browser never calls cpg.computer directly.
 *
 * When that path is down (no credential yet, the dev bypass on a laptop, our API unreachable), ticks are kept in
 * this browser instead, and sent up on the next load that reaches the API. The page works either way. Under the
 * dev bypass (a laptop, signed in as nobody) the browser keeps them and never asks. In the public demo (CPG_DEMO=1)
 * they last until the page closes: kept nowhere, sent nowhere.
 */
import { useCallback, useEffect, useState } from 'react'
import { apiGet, apiPost, type ApiAuth } from '../app/api'
import { useSession } from '../app/session'

export interface Tick {
  mission: string
  at: string
  how: 'auto' | 'self'
}

export interface TickStore {
  list(): Promise<Tick[]>
  add(mission: string, how: Tick['how']): Promise<void>
}

type KeyValueStore = Pick<Storage, 'getItem' | 'setItem'> & Partial<Pick<Storage, 'removeItem'>>

const KEY = 'cpg.learn.ticks'

export function browserTicks(storage: KeyValueStore | undefined): TickStore & { clear(): void } {
  const read = (): Tick[] => {
    try {
      const parsed: unknown = JSON.parse(storage?.getItem(KEY) ?? '[]')
      return Array.isArray(parsed) ? (parsed as Tick[]) : []
    } catch {
      return []
    }
  }
  return {
    async list() {
      return read()
    },
    async add(mission, how) {
      const all = read()
      if (all.some((t) => t.mission === mission)) return
      all.push({ mission, at: new Date().toISOString(), how })
      try {
        storage?.setItem(KEY, JSON.stringify(all))
      } catch {
        // storage blocked: the tick lasts until the page closes
      }
    },
    clear() {
      try {
        storage?.removeItem?.(KEY)
      } catch {
        // storage blocked: nothing to clear
      }
    },
  }
}

/** The app server's answers (it passes cpg.computer's through). */
interface ApiTick {
  missionId: string
  how: Tick['how']
  at: string
}

/** How the API store reaches the app's server. `http(auth)` in the app; tests pass fakes. */
export interface TicksApi {
  list(): Promise<{ ticks: ApiTick[] }>
  add(missionId: string, how: Tick['how']): Promise<unknown>
}

export const TICKS_PATH = '/api/learn/ticks'

/** The real one: through the app's own /api, signed in like every other call. */
export function httpTicksApi(auth: () => ApiAuth): TicksApi {
  return {
    list: () => apiGet<{ ticks: ApiTick[] }>(TICKS_PATH, auth()),
    add: (missionId, how) => apiPost(TICKS_PATH, { missionId, how }, auth()),
  }
}

/** After a failure, wait this long before asking the API again, so a laptop without it doesn't ask on every page. */
export const RETRY_AFTER_MS = 60_000

/**
 * Ticks on cpg.computer's API, with this browser as the fallback. Reads are shared while one is running and kept
 * until the next tick. Ticks this browser kept while the API was down are sent up once it answers, then forgotten.
 */
export function apiTicks(api: TicksApi, local: TickStore & { clear(): void }, now: () => number = Date.now): TickStore {
  let cached: Promise<Tick[]> | null = null
  let downUntil = 0
  const down = () => now() < downUntil
  const fail = () => {
    downUntil = now() + RETRY_AFTER_MS
  }

  async function load(): Promise<Tick[]> {
    if (down()) return local.list()
    let server: Tick[]
    try {
      server = (await api.list()).ticks.map((t) => ({ mission: t.missionId, how: t.how, at: t.at }))
    } catch {
      fail()
      return local.list()
    }
    const held = (await local.list()).filter((t) => !server.some((s) => s.mission === t.mission))
    if (!held.length) {
      local.clear()
      return server
    }
    try {
      for (const t of held) await api.add(t.mission, t.how)
      local.clear()
    } catch {
      // Kept for the next load; show them meanwhile.
    }
    return [...server, ...held]
  }

  return {
    list() {
      cached ??= load().finally(() => {
        // A read that fell back to this browser isn't kept, so the next page asks again once the wait is over.
        if (down()) cached = null
      })
      return cached
    },
    async add(mission, how) {
      cached = null
      if (!down()) {
        try {
          await api.add(mission, how)
          return
        } catch {
          fail()
        }
      }
      await local.add(mission, how)
    },
  }
}

function safeLocalStorage(): Storage | undefined {
  try {
    return typeof localStorage === 'undefined' ? undefined : localStorage
  } catch {
    return undefined
  }
}

/**
 * The session the store signs in with. Kept current by useTicks, so the store can live outside React. No `end`:
 * a tick that fails never signs the person out.
 */
let session: ApiAuth = { getToken: async () => undefined }

/** The dev bypass on a laptop: signed in as nobody, so there's no one to keep ticks for at cpg.computer. */
const bypass = typeof __DEV_BYPASS_AUTH__ !== 'undefined' && __DEV_BYPASS_AUTH__

/** The demo: shared by everyone who opens it, so a tick stays on this page and goes nowhere. */
const demo = typeof __CPG_DEMO__ !== 'undefined' && __CPG_DEMO__

export const tickStore: TickStore = demo
  ? browserTicks(memoryStore())
  : bypass
    ? browserTicks(safeLocalStorage())
    : apiTicks(httpTicksApi(() => session), browserTicks(safeLocalStorage()))

/** A key-value store in this page's memory only. */
export function memoryStore(): KeyValueStore {
  const m = new Map<string, string>()
  return { getItem: (k) => m.get(k) ?? null, setItem: (k, v) => void m.set(k, v), removeItem: (k) => void m.delete(k) }
}

const listeners = new Set<() => void>()

/** The ticks, kept current across every mission on screen. */
export function useTicks() {
  const { getToken, refresh } = useSession()
  session = { getToken, refresh }
  const [ticks, setTicks] = useState<Tick[]>([])
  useEffect(() => {
    let live = true
    const load = () => void tickStore.list().then((t) => live && setTicks(t))
    load()
    listeners.add(load)
    return () => {
      live = false
      listeners.delete(load)
    }
  }, [])
  const tick = useCallback(async (mission: string, how: Tick['how']) => {
    await tickStore.add(mission, how)
    for (const l of listeners) l()
  }, [])
  const done = useCallback((mission: string) => ticks.some((t) => t.mission === mission), [ticks])
  return { ticks, done, tick }
}
